import { atom } from "jotai";
import moment from "moment";

import DaysServices from "utils/days";

import {
  TDocItem,
  TDocItemToDraw,
  TDocument,
  TDocumentsFilters,
  TDocumentsOperation,
  TDocumentToDraw,
  TOpenedDocItemState,
  TOpenedDocumentState,
} from "../../types/documents";
import { PrintTemplate } from "../../types";

const selectedDocumentTypeAtom = atom<TDocumentsOperation | null>(null);

const selectedDocumentTypeTemplatesAtom = atom<PrintTemplate[] | null>([]);

const documentsFiltersInit: TDocumentsFilters = {
  page: 1,
  pageSize: 1000,
  shouldReplace: true,
  dateFrom: DaysServices.getDayStart(
    new Date(moment().subtract(1, "month").toDate())
  ),
  dateTo: DaysServices.getDayEnd(new Date(moment().toDate())),
};

const documentsFiltersAtom = atom<TDocumentsFilters, any, void>(
  documentsFiltersInit,
  (get, set, update) => {
    if (update.shouldReplace) {
      set(documentsFiltersAtom, update);
    } else {
      const prevObj = get(documentsFiltersAtom);
      set(documentsFiltersAtom, {
        ...prevObj,
        page: 1,
        shouldUpdate: true,
        ...update,
      });
    }
  }
);

const documentsAtom = atom<TDocument[], TDocument[], void>(
  [],
  (get, set, update) => {
    set(documentsAtom, update);
    set(
      documentsToDrawAtom,
      update.map((i) => ({
        ...i,
        isConducted: i.ok === 1,
        number: i.docid,
        creationTime: moment(i.time).format("DD.MM.YYYY HH:mm:ss"),
        sum: i.sum,
        stock: i.store?.name,
        stock2: i.store2?.name,
        comment: i.comment,
      }))
    );
  }
);

const documentsToDrawAtom = atom<TDocumentToDraw>([]);

const selectedDocumentAtom = atom<TDocument | null>(null);

const openedDocumentAtom = atom<TDocument | null, TDocument | null, void>(
  null,
  (get, set, update: TDocument | null) => {
    set(openedDocumentAtom, update);
    if (update === null) {
      set(openedDocumentStateAtom, {});
    } else {
      set(openedDocumentStateAtom, {
        docid: update.docid,
        time: new Date(update.time),
        comment: update.comment,
        storeid: update.storeid,
        store2id: update.store2id,
        priceupdated: Boolean(update.priceupdated),
      });
    }
  }
);

const openedDocumentStateAtom = atom<TOpenedDocumentState, any, void>(
  {},
  (get, set, update) => {
    set(openedDocumentStateAtom, {
      ...get(openedDocumentStateAtom),
      ...update,
    });
  }
);

const documentLoadingAtom = atom(false);

// ============= Doc Items =============
const docItemsAtom = atom<TDocItem[], TDocItem[], void>(
  [],
  (get, set, update) => {
    set(docItemsAtom, update);
    set(
      docItemsToDrawAtom,
      update.map((i) => ({
        ...i,
        item: i.item?.name,
        item2: i.item2?.name,
        itemcode: i.item?.code,
        measure: i.measure?.name,
        measure2: i.measure2?.name,
        pricelist: i.pricelist?.name,
        price: i.price,
        amount: i.amount,
        difference: i.amount - i.realamount,
        total: i.total,
        salepercent: i.salepercent,
        frontprice: i.frontprice,
        itemcomment: i.itemcomment,
      }))
    );
  }
);

const docItemsToDrawAtom = atom<TDocItemToDraw[]>([]);

const selectedDocItemAtom = atom<TDocItem | null>(null);

const openedDocItemAtom = atom<TDocItem | null, TDocItem | null, void>(
  null,
  (get, set, update: TDocItem | null) => {
    set(openedDocItemAtom, update);
    if (update === null) {
      set(openedDocItemStateAtom, {});
    } else {
      set(openedDocItemStateAtom, {
        docitemid: update.docitemid,
        itemid: update.item?.itemid,
        item2id: update.item2?.itemid,
        measureid: update.measure?.measureid,
        measure2id: update.measure2?.measureid,
        pricelistid: update.pricelist?.pricelistid,
        price: update.price,
        amount: update.amount || 0,
        realamount: update.realamount,
        difference: +update.amount - +update.realamount,
        total: update.total,
        salepercent: update.salepercent,
        frontprice: update.frontprice,
        itemcomment: update.itemcomment,
      });
    }
  }
);

const openedDocItemStateAtom = atom<TOpenedDocItemState, any, void>(
  {},
  (get, set, update) => {
    set(
      openedDocItemStateAtom,
      update?.shouldReplace
        ? update
        : {
            ...get(openedDocItemStateAtom),
            ...update,
          }
    );
  }
);

const docItemsToDeleteAtom = atom<TDocItem[]>([]);

export {
  documentsFiltersInit,
  selectedDocumentTypeAtom,
  documentsFiltersAtom,
  documentsAtom,
  documentsToDrawAtom,
  selectedDocumentAtom,
  openedDocumentAtom,
  openedDocumentStateAtom,
  docItemsAtom,
  docItemsToDrawAtom,
  selectedDocItemAtom,
  openedDocItemAtom,
  openedDocItemStateAtom,
  docItemsToDeleteAtom,
  selectedDocumentTypeTemplatesAtom,
  documentLoadingAtom,
};
